import {Dispatch,SetStateAction,useEffect,useMemo,useRef,} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FilterValue, TIME_FILTERS } from "../../../components/types/types";

interface Params {
  filterChoose: FilterValue[];
  setFilterChoose: Dispatch<SetStateAction<FilterValue[]>>;

  dateSelected: Date | null;
  setDateSelected: Dispatch<SetStateAction<Date | null>>;

  hourSelected: string;
  setHourSelected: Dispatch<SetStateAction<string>>;

  currentNeighborhood: string[];
  setCurrentNeighborhood: Dispatch<SetStateAction<string[]>>;
  seoFilterValue?: string | null;
}


const formatDateForUrl = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const parseDateFromUrl = (value: string | null): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year ||date.getMonth() !== month - 1 ||date.getDate() !== day) return null;
  return date;
};


const hourStringToMinutes = (hour: string): number | null => {
  if (!hour) return null;
  const match = /^(\d{1,2})(?:h|:)(\d{2})$/i.exec(hour);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) ||!Number.isInteger(minutes) ||hours < 0 ||hours > 23 ||minutes < 0 ||minutes > 59) return null;
  return hours * 60 + minutes;
};

const minutesToHourString = (value: string | null): string => {
  if (!value) return "";
  const total = Number(value);
  if (!Number.isInteger(total) ||total < 0 ||total > 1439) return "";
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${String(hours).padStart(2, "0")}h${String(minutes).padStart(2, "0")}`;
};
const TIME_FILTER_TO_URL: Record<string, string> = {
"Ouvert maintenant": "now",
  "Ouvert ce midi": "today_lunch",
  "Ouvert ce soir": "today_dinner",
  "Ouvert demain midi": "tomorrow_lunch",
  "Ouvert demain soir": "tomorrow_dinner",
};
const URL_TO_TIME_FILTER: Record<string, string> = {
    now: "Ouvert maintenant",
  today_lunch: "Ouvert ce midi",
  today_dinner: "Ouvert ce soir",
  tomorrow_lunch: "Ouvert demain midi",
  tomorrow_dinner: "Ouvert demain soir",
};
const sameStringArray = (a: string[], b: string[]) => a.length === b.length && a.every((value, index) => value === b[index]);
const sameFilters = (a: FilterValue[],b: FilterValue[]) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};
interface BuildSearchQueryParams {
  filterChoose: FilterValue[];
  dateSelected: Date | null;
  hourSelected: string;
  currentNeighborhood: string[];
  seoFilterValue?: string | null;
}

export const buildSearchQuery = ({
  filterChoose,
  dateSelected,
  hourSelected,
  currentNeighborhood,
  seoFilterValue = null,
}: BuildSearchQueryParams): string => {
  const params = new URLSearchParams();
  const timeFilter = filterChoose.find(
    (filter): filter is string =>
        typeof filter === "string" &&
        TIME_FILTERS.has(filter)
    );
    if (timeFilter) {
        const timePreset = TIME_FILTER_TO_URL[timeFilter];
        if (timePreset) {
            params.set("timePreset", timePreset);
        }
    }

  filterChoose
    .filter(
      (filter): filter is string =>
        typeof filter === "string"
    )
    .filter(
        (filter) =>
        !TIME_FILTERS.has(filter)
    )
    .filter(
      (filter) =>
        !seoFilterValue ||
        filter !== seoFilterValue
    )
    .forEach((filter) => {
      params.append("f", filter);
    });

  const priceFilter = filterChoose.find(
    (filter) => Array.isArray(filter)
  );

  if (Array.isArray(priceFilter) &&priceFilter.length === 2) {
    params.set("priceMin", String(priceFilter[0]));
    params.set("priceMax", String(priceFilter[1]));
  }

  if (dateSelected) {
    params.set(
      "date",
      formatDateForUrl(dateSelected)
    );
  }
  const hourInMinutes = hourStringToMinutes(hourSelected);
  if (hourInMinutes !== null) {
    params.set(
      "hour",
      String(hourInMinutes)
    );
  }
  currentNeighborhood.forEach((neighborhood) => {
    params.append(
      "neighborhood",
      neighborhood
    );
  });

  const search = params.toString();
  return search ? `?${search}` : "";
};
export function useSearchQuerySync({
  filterChoose,
  setFilterChoose,

  dateSelected,
  setDateSelected,

  hourSelected,
  setHourSelected,

  currentNeighborhood,
  setCurrentNeighborhood,

  seoFilterValue,
}: Params) {
  const location = useLocation();
  const navigate = useNavigate();

  const applyingUrlRef = useRef(false);
  const hydratedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlFilters = params.getAll("f").map((value) => value.trim()).filter(Boolean);
    const timePreset = params.get("timePreset");
    const restoredTimeFilter = timePreset ? URL_TO_TIME_FILTER[timePreset] ?? null : null;
    const nextStringFilters = [
      ...(seoFilterValue ? [seoFilterValue] : []),
      ...urlFilters.filter(
        (filter) => filter !== seoFilterValue
      ),
      ...(restoredTimeFilter ? [restoredTimeFilter] : []),
    ];
    const priceMinRaw = params.get("priceMin");
    const priceMaxRaw = params.get("priceMax");
    let priceFilter: FilterValue[] = [];
    if (priceMinRaw !== null && priceMaxRaw !== null) {
      const min = Number(priceMinRaw);
      const max = Number(priceMaxRaw);
      if (Number.isFinite(min) &&Number.isFinite(max) &&min >= 0 &&max >= min) {
        priceFilter = [[min, max] as FilterValue];
      }
    }

    const nextFilters: FilterValue[] = [...nextStringFilters,...priceFilter,];
    const nextDate = parseDateFromUrl(
      params.get("date")
    );
    const nextHour = minutesToHourString(
      params.get("hour")
    );
    const nextNeighborhoods = params.getAll("neighborhood").map((value) => value.trim()).filter(Boolean);
    applyingUrlRef.current = true;
    setFilterChoose((prev) =>
      sameFilters(prev, nextFilters) ? prev : nextFilters
    );
    setDateSelected((prev) => {
      const prevTime = prev?.getTime() ?? null;
      const nextTime = nextDate?.getTime() ?? null;
      return prevTime === nextTime ? prev : nextDate;
    });
    setHourSelected((prev) => prev === nextHour ? prev : nextHour);
    setCurrentNeighborhood((prev) => sameStringArray(prev, nextNeighborhoods) ? prev : nextNeighborhoods);
    hydratedRef.current = true;
    queueMicrotask(() => {
      applyingUrlRef.current = false;
    });
  }, [location.search,seoFilterValue,setFilterChoose,setDateSelected,setHourSelected,setCurrentNeighborhood,]);


  const querySignature = useMemo(() => {
    const stringFilters = filterChoose
      .filter(
        (filter): filter is string =>
          typeof filter === "string"
      )
      .filter(
        (filter) =>
            !TIME_FILTERS.has(filter)
        )
      .filter(
        (filter) => !seoFilterValue || filter !== seoFilterValue
      );

    const priceFilter = filterChoose.find(
      (filter) => Array.isArray(filter)
    );
    const timeFilter = filterChoose.find(
    (filter): filter is string =>
        typeof filter === "string" &&
        TIME_FILTERS.has(filter)
    );

    return JSON.stringify({
      filters: stringFilters,
       timePreset: timeFilter ? (TIME_FILTER_TO_URL[timeFilter] ?? null) : null,
      price: priceFilter ?? null,
      date: dateSelected ? formatDateForUrl(dateSelected) : null,
      hour: hourSelected,
      neighborhoods: currentNeighborhood,
    });
  }, [filterChoose,seoFilterValue,dateSelected,hourSelected,currentNeighborhood,]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    if (applyingUrlRef.current) return;

    const nextSearch = buildSearchQuery({filterChoose,dateSelected,hourSelected,currentNeighborhood,seoFilterValue,});
    if (nextSearch === location.search) {
        return;
    }

    navigate(
        {pathname: location.pathname,search: nextSearch},{replace: true,}
    );
  }, [querySignature,location.pathname,location.search,navigate]);
}